import { FocusScope } from '@react-aria/focus';
import cx from 'classnames';
import FocusLock from 'react-focus-lock';

import { useFeatureGate } from '@trello/feature-gate-client';
import { getTestId, type ColorPaletteTestIds } from '@trello/test-ids';

import { ColorTile } from './ColorTile';
import { type Color } from './formatColor';

import styles from './ColorPalette.less';

interface ColorPaletteProps<T> {
  onClick: (color: T) => void;
  colorOptions: T[];
  selectedColor: T | null;
  isSourceSidebarMenu?: boolean;
}

export const ColorPalette = <T extends Color>({
  onClick,
  colorOptions,
  selectedColor,
  isSourceSidebarMenu,
}: ColorPaletteProps<T>) => {
  const { value: isReactFocusLockEnabled } = useFeatureGate(
    'ghost_use_react_focus_lock',
  );

  return (
    <div
      className={cx(styles.palette, {
        [styles.noHorizontalMargin]: isSourceSidebarMenu,
      })}
      data-testid={getTestId<ColorPaletteTestIds>('color-palette')}
    >
      {isReactFocusLockEnabled ? (
        // The default display: block from FocusLock would break the color palette style, so display: contents is used to correct the layout.
        <FocusLock className={styles.focusLock} disabled={true}>
          {colorOptions.map((color) => (
            <ColorTile
              key={color}
              color={color}
              onClick={onClick}
              isSelected={selectedColor === color}
            />
          ))}
        </FocusLock>
      ) : (
        <FocusScope>
          {colorOptions.map((color) => (
            <ColorTile
              key={color}
              color={color}
              onClick={onClick}
              isSelected={selectedColor === color}
            />
          ))}
        </FocusScope>
      )}
    </div>
  );
};
